import React, { FunctionComponent, useMemo, useState } from 'react';
import { Box, Button, Typography, useTheme } from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { ACard, AStringCurrentGauge, Skeleton } from '@atiautomacao/ati-ui-library';
import {
    ACTIVE_TELEOBJECT_ALARMS_ERROR_MESSAGE,
    FindActiveTeleObjectAlarmsByEquipmentId,
    FindNominalPowerByEquipmentIdReactQuery,
    FindStringCurrentSnapshotByEquipmentId,
    getAlarmedTeleObjectIdSet,
    getMaxStringCurrentFromNominalPower,
    useUpdateMaxStringCurrentMutation,
} from '../../InverterReactQuery';
import {
    buildGaugeViews,
    buildSectionSummary,
    isMaxCurrentConfigured,
    parseStringCurrentPoints,
} from './stringCurrentsModel';
import ConfigureMaxCurrentModal from './ConfigureMaxCurrentModal';

export interface StringCurrentsSectionProps {
    equipmentId: number;
}

const ERROR_MESSAGE = 'Não foi possível carregar as correntes das strings. Tente novamente.';
const EMPTY_MESSAGE = 'Nenhuma corrente de string encontrada.';

/**
 * Snapshot already applies SGD-1933 on the backend (expired/missing → null value).
 * Null value drives stale visuals (`- A`, no fill).
 */
function isSnapshotValueStale(value: number | null | undefined): boolean {
    return value === null || value === undefined || Number.isNaN(Number(value));
}

interface SectionFeedbackProps {
    type: 'error' | 'empty';
    message?: string;
}

const SectionFeedback: FunctionComponent<SectionFeedbackProps> = ({ type, message }) => (
    <Box
        role={type === 'error' ? 'alert' : 'status'}
        sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: 160,
            textAlign: 'center',
            width: '100%',
        }}
    >
        <Typography variant="body2" color={type === 'error' ? 'error' : 'text.secondary'}>
            {message ?? (type === 'error' ? ERROR_MESSAGE : EMPTY_MESSAGE)}
        </Typography>
    </Box>
);

const StringCurrentsSection: FunctionComponent<StringCurrentsSectionProps> = ({ equipmentId }) => {
    const theme = useTheme();
    const [modalOpen, setModalOpen] = useState(false);

    const snapshotQuery = FindStringCurrentSnapshotByEquipmentId(equipmentId);
    const nominalPowerQuery = FindNominalPowerByEquipmentIdReactQuery(equipmentId);
    const activeAlarmsQuery = FindActiveTeleObjectAlarmsByEquipmentId(equipmentId);
    const updateMaxCurrent = useUpdateMaxStringCurrentMutation(equipmentId);

    const maxStringCurrent = getMaxStringCurrentFromNominalPower(nominalPowerQuery.data);
    const alarmedIds = useMemo(
        () => getAlarmedTeleObjectIdSet(activeAlarmsQuery.data),
        [activeAlarmsQuery.data]
    );

    const points = useMemo(
        () => parseStringCurrentPoints(snapshotQuery.data?.data),
        [snapshotQuery.data]
    );

    const gauges = useMemo(() => {
        const staleByTeleObjectId = new Set<number>();
        for (const point of points) {
            if (point.teleObjectId == null) {
                continue;
            }
            if (isSnapshotValueStale(point.value)) {
                staleByTeleObjectId.add(point.teleObjectId);
            }
        }
        return buildGaugeViews(points, maxStringCurrent, {
            staleByTeleObjectId,
            alarmByTeleObjectId: alarmedIds,
        });
    }, [points, maxStringCurrent, alarmedIds]);

    const summary = buildSectionSummary(gauges.length, maxStringCurrent);
    const isLoading =
        equipmentId > 0 &&
        (snapshotQuery.isLoading ||
            nominalPowerQuery.isLoading ||
            (activeAlarmsQuery.isLoading && !activeAlarmsQuery.data));
    const hasCurrentsError = snapshotQuery.isError || nominalPowerQuery.isError;

    let sectionContent: React.ReactNode;
    if (isLoading) {
        sectionContent = (
            <Skeleton animation="wave" height={160} variant="rectangular" width="100%" />
        );
    } else if (activeAlarmsQuery.isError) {
        sectionContent = (
            <SectionFeedback type="error" message={ACTIVE_TELEOBJECT_ALARMS_ERROR_MESSAGE} />
        );
    } else if (hasCurrentsError) {
        sectionContent = <SectionFeedback type="error" />;
    } else if (gauges.length === 0) {
        sectionContent = <SectionFeedback type="empty" />;
    } else {
        sectionContent = (
            <Box
                component="ul"
                role="list"
                aria-label="Correntes das strings monitoradas"
                data-testid="string-currents-section-grid"
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(122px, 1fr))',
                    gap: theme.spacing(2),
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    width: '100%',
                }}
            >
                {gauges.map((gauge) => (
                    <Box
                        component="li"
                        role="listitem"
                        key={gauge.teleObjectId}
                        sx={{ display: 'flex', minWidth: 0 }}
                    >
                        <AStringCurrentGauge
                            name={gauge.name}
                            valueAmps={gauge.currentAmps}
                            fillRatio={gauge.fillRatio}
                            visualState={gauge.visualState}
                        />
                    </Box>
                ))}
            </Box>
        );
    }

    const handleSave = async (value: number) => {
        try {
            await updateMaxCurrent.mutateAsync(value);
        } catch {
            // The mutation reports the API error through the snackbar.
            return;
        }
        setModalOpen(false);
    };

    return (
        <>
            <ACard
                title="Correntes das Strings"
                subTitle={summary}
                ariaLabel="dashboard-station-inverter-string-currents-gauges"
                headerControlsPosition="header"
                styleProps={{
                    cardStyle: {
                        minHeight: 200,
                        height: 'auto',
                        borderRadius: theme.shape.borderRadius * 2,
                    },
                    headerStyle: {
                        padding: theme.spacing(3, 3, 0),
                        alignItems: 'flex-start',
                    },
                    cardTitleStyle: {
                        padding: 0,
                        gap: 0,
                    },
                    contentStyle: {
                        padding: theme.spacing(2, 3, 3),
                        height: 'auto',
                        display: 'block',
                    },
                    titleStyle: {
                        ...theme.typography.h6,
                        alignItems: 'center',
                        display: 'flex',
                        padding: 0,
                    },
                    subTitleStyle: {
                        ...theme.typography.body2,
                        color: theme.palette.text.secondary,
                        display: 'block',
                        letterSpacing: '0.4px',
                        lineHeight: 1.66,
                        padding: theme.spacing(0.375, 0, 0),
                    },
                }}
                headerControls={
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SettingsOutlinedIcon />}
                        aria-label="dashboard-station-inverter-string-currents-config"
                        onClick={() => setModalOpen(true)}
                        sx={{
                            ...theme.typography.button,
                            fontSize: theme.typography.pxToRem(15),
                            lineHeight: theme.typography.pxToRem(26),
                            px: theme.spacing(2.75),
                            py: theme.spacing(1),
                            mt: theme.spacing(1),
                            whiteSpace: 'nowrap',
                        }}
                    >
                        Configurar limite máximo
                    </Button>
                }
            >
                {sectionContent}
            </ACard>
            <ConfigureMaxCurrentModal
                open={modalOpen}
                initialValue={isMaxCurrentConfigured(maxStringCurrent) ? maxStringCurrent : null}
                saving={updateMaxCurrent.isLoading}
                onClose={() => setModalOpen(false)}
                onSave={handleSave}
            />
        </>
    );
};

export default StringCurrentsSection;
